/**
 * Which month's spend is on screen (M1).
 *
 * ── Why this did not exist ──────────────────────────────────────────────
 *
 * `GET /orgs/:id/usage` and `/usage/me` have BOTH accepted a `period`
 * parameter all along — `budgetApi.getOrgUsage(tenantId, p)` passes it — and no
 * customer-facing screen ever sent one. Every money screen was pinned to the
 * current month, so "what did we spend last month?" — the first question anyone
 * asks when an invoice arrives — was unanswerable from inside the product.
 *
 * ── The bound, and why it is not a date input ───────────────────────────
 *
 * A free-form date field invites months that have no data and answers them with
 * an empty page, which reads as breakage rather than as "nothing happened". The
 * list is therefore BOUNDED: the current month plus `MONTHS_BACK`, and never
 * before `since` — the tenant's first recorded activity, which the caller
 * passes when it knows it. A tenant created last week gets one option, and the
 * control hides itself rather than offering a picker with a single entry.
 *
 * Periods are `YYYY-MM`, the same shape the endpoints and
 * `notification_data.period` use. Formatting for display goes through
 * `Intl.DateTimeFormat` on the FIRST of the month at UTC noon — a bare
 * `new Date('2026-08')` parses as UTC midnight and renders as the previous
 * month for every reader west of Greenwich.
 */
import { useMemo } from 'react'
import { useTranslation } from '@papan/i18n'

/** How far back the picker offers. A year of context is what an annual
 *  invoice or a "why was March expensive" question needs; beyond that the
 *  list stops being scannable. */
const MONTHS_BACK = 11

/** `YYYY-MM` for a Date, in UTC — the period key is not local-time-sensitive. */
export function periodKey(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

/** The options a picker should offer, newest first. Exported for testing —
 *  the bounds are the part worth pinning, not the markup. */
export function monthOptions(now: Date, since?: string | null): string[] {
  const out: string[] = []
  for (let i = 0; i <= MONTHS_BACK; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
    const key = periodKey(d)
    // `since` is a period key too, so a string compare is a chronological one
    // — `YYYY-MM` sorts correctly as text, which is why the wire uses it.
    if (since && key < since) break
    out.push(key)
  }
  return out
}

export function MonthPicker({
  value,
  onChange,
  since,
  now = new Date(),
}: {
  value: string
  onChange: (period: string) => void
  /** Earliest period with data (`YYYY-MM`), when the caller knows it. */
  since?: string | null
  /** Injectable for tests. */
  now?: Date
}) {
  const { t } = useTranslation('core-ui')
  const options = useMemo(() => {
    const opts = monthOptions(now, since)
    // The value on screen must always be selectable, even if it came from a
    // link to a month outside the window — otherwise the select renders blank
    // and the reader cannot tell what they are looking at.
    return opts.includes(value) ? opts : [value, ...opts]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [since, value, now.getTime()])

  // One option is not a choice. A tenant a fortnight old would otherwise get a
  // control that does nothing.
  if (options.length < 2) return null

  return (
    <label className="flex items-center gap-1.5">
      <span className="sr-only">{t('settings.budget.month.label')}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={t('settings.budget.month.label')}
        className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500"
      >
        {options.map((p) => (
          <option key={p} value={p}>{formatPeriod(p)}</option>
        ))}
      </select>
    </label>
  )
}

/** `2026-08` → "August 2026", in the reader's locale. */
export function formatPeriod(period: string): string {
  const [y, m] = period.split('-').map(Number)
  if (!y || !m) return period
  // TWO independent protections against the same bug, and that is deliberate.
  //
  // The bug: the 1st at MIDNIGHT UTC is the last day of the PREVIOUS month for
  // every reader at a negative offset, so a US admin's August page would be
  // labelled July. Either the noon offset here OR `timeZone: 'UTC'` on the
  // formatter below prevents it; both are present because a future caller may
  // reuse this Date without pinning a zone, or reuse the formatter pattern
  // without the offset.
  //
  // Worth knowing before "simplifying" one away: mutation-checking shows the
  // test cannot fail on either removal ALONE — each suffices — and fails only
  // when both go. So a green suite is not evidence that the one you kept is
  // the one doing the work.
  const d = new Date(Date.UTC(y, m - 1, 1, 12))
  try {
    return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', timeZone: 'UTC' }).format(d)
  } catch {
    return period
  }
}
